import type {
  Asset,
  BenefitEnrollment,
  BenefitPlan,
  Candidate,
  CandidateStage,
  CompensationOverview,
  CompensationRecord,
  Course,
  EmployeeLoan,
  EmployeeRelationsCase,
  JobRequisition,
  OffboardingCase,
  OnboardingCase,
  PerformanceReview,
  PermissionCatalogGroup,
  ProbationReview,
  ReviewCycle,
  RoleDefinition,
  TrainingRecord,
  TrainingSession,
  WorkflowTask,
} from '@micarahr/shared-types';
import {
  insert,
  paginate,
  readAll,
  readOne,
  update,
  writeAll,
  type Page,
  type PageRequest,
} from './collections';
import { errors, runtime, serviceCall } from './runtime';
import { recordAudit, type AuditActor } from './audit';
import { requireSession } from './auth.mock';
import { nextReference } from './ids';
import type { AdminEmployee, Department } from './directory';
import type { ConvertedEmployee } from './seed';
import { env } from '../config/env';
import { addMonthsToKey, extensionError, reviewDueFor } from '../lib/probation';
import { setBusinessTimeZone, todayInBusiness } from '../lib/business-date';
import {
  apiCompensationService,
  apiRecruitmentService,
  type CandidateDetail,
  type CandidateInterviewRecord,
  type InterviewOutcomeInput,
  type NewCandidateInput,
} from './talent.api';
import { apiRolePermissionService } from './access.api';
import { PERMISSION_GROUPS } from '@micarahr/permissions/web';
import { countLabel } from '@micarahr/shared-utils';
import { apiDocumentSummaryService } from './registries.api';
import {
  apiSettingsService,
  isServerSection,
  toAttendanceSection,
  toSection,
} from './settings.api';
import {
  apiAnnouncementService,
  apiAssetService,
  apiBenefitsService,
  apiDocumentAdminService,
  apiEmployeeRelationsService,
  apiLearningService,
  apiLeaveConfigService,
  apiOffboardingService,
  apiOnboardingService,
  apiLoanService,
  apiPerformanceService,
  apiPolicyService,
  apiProbationService,
  type OffboardingCandidate,
  type OffboardingInput,
} from './modules.api';
import { offboardingReasonLabel } from '../lib/people-labels';

export type { OffboardingCandidate, OffboardingInput } from './modules.api';
import {
  apiPayrollConfigService,
  type PayrollBracket,
  type PayrollConfigurationView,
  type PayrollTableKind,
  type PayrollTableVersion,
} from './payroll-config.api';

import { nextId } from './ids';

/**
 * The remaining administration modules.
 *
 * Grouped in one file because they share one shape: a filtered list, a record,
 * and a small number of state transitions that must produce an audit entry.
 * Splitting them into twenty near-identical files would hide that they are the
 * same thing, and make the one that drifts harder to spot.
 */

/* -------------------------------------------------------------------------- */
/* A generic list, so every module filters and paginates identically           */
/* -------------------------------------------------------------------------- */

function listOf<T extends { id: string }>(
  service: string,
  collection: Parameters<typeof readAll>[0],
  request: PageRequest,
  searchable: (row: T) => string,
  extraFilter?: (row: T) => boolean,
): Promise<Page<T>> {
  return serviceCall(service, () => {
    let rows = readAll<T>(collection);
    if (extraFilter) rows = rows.filter(extraFilter);
    return paginate(rows as unknown as Record<string, unknown>[], request, (row) =>
      searchable(row as unknown as T),
    ) as unknown as Page<T>;
  });
}

/* -------------------------------------------------------------------------- */
/* Documents and policies                                                      */
/* -------------------------------------------------------------------------- */

export interface EmployeeDocumentRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  typeId: string;
  name: string;
  fileName: string;
  status: 'valid' | 'expiring' | 'expired' | 'pending_verification' | 'missing';
  issuedOn: string | null;
  expiresOn: string | null;
  verifiedByName: string | null;
  verifiedAt: string | null;
  uploadedAt: string;
}

export interface PolicyRecord {
  id: string;
  title: string;
  category: string;
  version: string;
  effectiveDate: string;
  status: 'draft' | 'review' | 'published' | 'archived';
  requiresAcknowledgement: boolean;
  audience: string;
  summary: string;
}

export interface AnnouncementRecord {
  id: string;
  title: string;
  category: string;
  /** "normal", "important" or "urgent" — the API's field; absent in the prototype. */
  priority?: string;
  status: 'draft' | 'scheduled' | 'published' | 'expired' | 'archived';
  publishedAt: string | null;
  expiresOn: string | null;
  audience: string;
  requiresAcknowledgement: boolean;
  body: string;
}

const mockDocumentAdminService = {
  list: (filter: PageRequest & { status?: string; employeeId?: string }) =>
    listOf<EmployeeDocumentRecord>(
      'documentAdminService',
      'documents',
      filter,
      (row) => `${row.employeeName} ${row.name} ${row.fileName}`,
      (row) =>
        (!filter.status || filter.status === 'all' || row.status === filter.status) &&
        (!filter.employeeId || row.employeeId === filter.employeeId),
    ),

  /** One document, for a link that landed outside the loaded page. */
  async get(id: string): Promise<EmployeeDocumentRecord | null> {
    return serviceCall(
      'documentAdminService',
      () => readAll<EmployeeDocumentRecord>('documents').find((row) => row.id === id) ?? null,
    );
  },

  async summary() {
    return serviceCall('documentAdminService', () => {
      const rows = readAll<EmployeeDocumentRecord>('documents');
      return {
        total: rows.length,
        valid: rows.filter((row) => row.status === 'valid').length,
        expiring: rows.filter((row) => row.status === 'expiring').length,
        expired: rows.filter((row) => row.status === 'expired').length,
        unverified: rows.filter((row) => row.verifiedAt === null).length,
      };
    });
  },

  async verify(id: string, actor: AuditActor): Promise<EmployeeDocumentRecord> {
    return serviceCall('documentAdminService', async () => {
      const after = await update<EmployeeDocumentRecord>(
        'documents',
        id,
        (row) => ({ ...row, verifiedByName: actor.name, verifiedAt: runtime.now() }),
        'document',
      );
      await recordAudit({
        actor,
        module: 'documents',
        action: 'document.verified',
        summary: `Verified ${after.name} for ${after.employeeName}`,
        recordType: 'document',
        recordId: id,
        recordLabel: after.name,
        subjectEmployeeId: after.employeeId,
        severity: 'notice',
      });
      return after;
    });
  },
};

const mockPolicyService = {
  list: (filter: PageRequest & { status?: string }) =>
    listOf<PolicyRecord>(
      'policyService',
      'policies',
      filter,
      (row) => `${row.title} ${row.category} ${row.summary}`,
      (row) => !filter.status || filter.status === 'all' || row.status === filter.status,
    ),

  async publish(id: string, actor: AuditActor): Promise<PolicyRecord> {
    return serviceCall('policyService', async () => {
      const before = readOne<PolicyRecord>('policies', id);
      if (!before) throw errors.notFound('policy');
      if (before.status === 'published') {
        throw errors.precondition('This policy version is already published.');
      }
      const after = await update<PolicyRecord>(
        'policies',
        id,
        (row) => ({ ...row, status: 'published' }),
        'policy',
      );
      await recordAudit({
        actor,
        module: 'policies',
        action: 'policy.published',
        summary: `Published ${after.title} v${after.version}`,
        recordType: 'policy',
        recordId: id,
        recordLabel: `${after.title} v${after.version}`,
        before: { status: before.status },
        after: { status: 'published' },
        severity: 'sensitive',
      });
      return after;
    });
  },

  /** Acknowledgement progress, computed rather than stored so it cannot drift. */
  async acknowledgementProgress(id: string) {
    return serviceCall('policyService', () => {
      const employees = readAll<{ id: string; employmentStatus: string }>('employees').filter(
        (row) => row.employmentStatus === 'active' || row.employmentStatus === 'probationary',
      );
      const acknowledged = readAll<{ policyId: string; employeeId: string }>(
        'acknowledgements',
      ).filter((row) => row.policyId === id);
      return {
        required: employees.length,
        acknowledged: acknowledged.length,
        outstanding: employees.length - acknowledged.length,
      };
    });
  },
};

const mockAnnouncementAdminService = {
  list: (filter: PageRequest & { status?: string }) =>
    listOf<AnnouncementRecord>(
      'announcementAdminService',
      'announcements',
      filter,
      (row) => `${row.title} ${row.category} ${row.body}`,
      (row) => !filter.status || filter.status === 'all' || row.status === filter.status,
    ),

  async publish(id: string, actor: AuditActor): Promise<AnnouncementRecord> {
    return serviceCall('announcementAdminService', async () => {
      const after = await update<AnnouncementRecord>(
        'announcements',
        id,
        (row) => ({ ...row, status: 'published', publishedAt: runtime.now() }),
        'announcement',
      );
      await recordAudit({
        actor,
        module: 'announcements',
        action: 'announcement.published',
        summary: `Published “${after.title}” to ${after.audience}`,
        recordType: 'announcement',
        recordId: id,
        recordLabel: after.title,
        severity: 'notice',
      });
      return after;
    });
  },
};

/* -------------------------------------------------------------------------- */
/* Recruitment                                                                 */
/* -------------------------------------------------------------------------- */

/** Stage transitions. A candidate cannot jump from applied to hired (§27). */
const STAGE_ORDER: CandidateStage[] = [
  'applied',
  'screening',
  'assessment',
  'interview',
  'final_interview',
  'reference_check',
  'offer',
  'hired',
];

export function canMoveStage(from: CandidateStage, to: CandidateStage): boolean {
  if (to === 'rejected' || to === 'withdrawn') return from !== 'hired';
  if (from === 'rejected' || from === 'withdrawn') return to === 'applied';
  const fromIndex = STAGE_ORDER.indexOf(from);
  const toIndex = STAGE_ORDER.indexOf(to);
  if (fromIndex === -1 || toIndex === -1) return false;
  // Forward one step, or back to any earlier stage to correct a mistake.
  return toIndex === fromIndex + 1 || toIndex < fromIndex;
}

const mockRecruitmentService = {
  requisitions: (filter: PageRequest & { status?: string }) =>
    listOf<JobRequisition>(
      'recruitmentService',
      'requisitions',
      filter,
      (row) => `${row.title} ${row.referenceCode} ${row.departmentName}`,
      (row) => !filter.status || filter.status === 'all' || row.status === filter.status,
    ),

  async createRequisition(
    input: {
      title: string;
      departmentId: string;
      departmentName: string;
      positionId?: string | null;
      headcount: number;
      employmentType: string;
      workLocationId: string | null;
      workLocationName: string | null;
      hiringManagerEmployeeId: string | null;
      hiringManagerName: string | null;
      reason: string;
      targetStartDate: string | null;
      budgetNote: string | null;
    },
    actor: AuditActor,
  ): Promise<JobRequisition> {
    return serviceCall('recruitmentService', async () => {
      if (!input.title.trim()) throw errors.validation('A requisition needs a job title.');
      if (input.headcount < 1) throw errors.validation('Headcount must be at least one.');
      if (!input.reason.trim()) {
        throw errors.validation('Give the reason this role is being opened.');
      }

      const year = new Date().getFullYear();
      const existing = readAll<JobRequisition>('requisitions');
      const requisition: JobRequisition = {
        ...input,
        id: nextId('req'),
        referenceCode: `REQ-${year}-${String(existing.length + 1).padStart(4, '0')}`,
        positionId: input.positionId ?? null,
        filledCount: 0,
        // A requisition is a request for headcount, so it starts as one.
        status: 'pending_approval',
        createdAt: runtime.now(),
        approvedAt: null,
      };
      await insert('requisitions', requisition);

      await recordAudit({
        actor,
        module: 'recruitment',
        action: 'requisition.created',
        summary: `Opened ${requisition.headcount} ${requisition.title} requisition(s) for ${requisition.departmentName}`,
        recordType: 'job_requisition',
        recordId: requisition.id,
        recordLabel: requisition.referenceCode,
        after: { title: input.title, headcount: input.headcount },
        severity: 'notice',
      });

      return requisition;
    });
  },

  /**
   * Approve a requisition.
   *
   * Approving is what makes a role recruitable, so it is a separate act from
   * raising one — the person asking for headcount is rarely the person who can
   * grant it.
   */
  async approveRequisition(id: string, actor: AuditActor): Promise<JobRequisition> {
    return serviceCall('recruitmentService', async () => {
      const before = readOne<JobRequisition>('requisitions', id);
      if (!before) throw errors.notFound('requisition');
      if (before.status !== 'pending_approval') {
        throw errors.precondition(
          `Only a requisition awaiting approval can be approved. This one is ${before.status.replace(/_/g, ' ')}.`,
        );
      }

      const after = await update<JobRequisition>(
        'requisitions',
        id,
        (row) => ({ ...row, status: 'open', approvedAt: runtime.now() }),
        'requisition',
      );

      await recordAudit({
        actor,
        module: 'recruitment',
        action: 'requisition.approved',
        summary: `Approved ${after.referenceCode} — ${after.headcount} ${after.title}`,
        recordType: 'job_requisition',
        recordId: id,
        recordLabel: after.referenceCode,
        before: { status: before.status },
        after: { status: 'open' },
        severity: 'notice',
      });

      return after;
    });
  },

  candidates: (filter: PageRequest & { stage?: string; requisitionId?: string }) =>
    listOf<Candidate>(
      'recruitmentService',
      'candidates',
      { pageSize: 200, ...filter },
      (row) => `${row.fullName} ${row.referenceCode} ${row.requisitionTitle} ${row.email}`,
      (row) =>
        (!filter.stage || filter.stage === 'all' || row.stage === filter.stage) &&
        (!filter.requisitionId || row.requisitionId === filter.requisitionId),
    ),

  async candidate(id: string): Promise<Candidate | null> {
    return serviceCall('recruitmentService', () => readOne<Candidate>('candidates', id));
  },

  async dashboard() {
    return serviceCall('recruitmentService', () => {
      const requisitions = readAll<JobRequisition>('requisitions');
      const candidates = readAll<Candidate>('candidates');
      const active = candidates.filter(
        (row) => !['hired', 'rejected', 'withdrawn'].includes(row.stage),
      );
      const hired = candidates.filter((row) => row.stage === 'hired');
      const daysToHire = hired.map((row) => {
        const applied = new Date(row.appliedAt).getTime();
        const decided = new Date(row.updatedAt).getTime();
        return Math.max(1, Math.round((decided - applied) / 86_400_000));
      });

      return {
        openRequisitions: requisitions.filter((row) => row.status === 'open').length,
        activeCandidates: active.length,
        interviewsThisWeek: candidates.reduce(
          (total, row) =>
            total +
            row.interviews.filter((interview) => {
              const days = (new Date(interview.scheduledAt).getTime() - Date.now()) / 86_400_000;
              return days >= 0 && days <= 7;
            }).length,
          0,
        ),
        offersPending: candidates.filter((row) => row.offer?.status === 'sent').length,
        averageDaysToHire:
          daysToHire.length === 0
            ? null
            : Math.round(daysToHire.reduce((sum, value) => sum + value, 0) / daysToHire.length),
      };
    });
  },

  async moveStage(id: string, stage: CandidateStage, actor: AuditActor): Promise<Candidate> {
    return serviceCall('recruitmentService', async () => {
      const before = readOne<Candidate>('candidates', id);
      if (!before) throw errors.notFound('candidate');
      if (!canMoveStage(before.stage, stage)) {
        throw errors.precondition(
          `A candidate cannot move from ${before.stage.replace(/_/g, ' ')} to ${stage.replace(/_/g, ' ')}.`,
        );
      }

      const after = await update<Candidate>(
        'candidates',
        id,
        (row) => ({ ...row, stage, updatedAt: runtime.now() }),
        'candidate',
      );

      await recordAudit({
        actor,
        module: 'recruitment',
        action: 'candidate.stage_changed',
        summary: `Moved ${after.fullName} to ${stage.replace(/_/g, ' ')}`,
        recordType: 'candidate',
        recordId: id,
        recordLabel: after.referenceCode,
        before: { stage: before.stage },
        after: { stage },
        severity: 'info',
      });

      return after;
    });
  },

  /*
   * The candidate panel, for the prototype data source.
   *
   * Deliberately thin: the rules that matter — which moves are legal, that an
   * offer carries terms, that hiring is an accepted offer — are the server's,
   * and these only keep the browser-only store coherent enough to click
   * through. The history is not stored here, so the panel shows the one fact
   * the prototype knows: when the candidate applied.
   */
  async candidateDetail(id: string): Promise<CandidateDetail> {
    return serviceCall('recruitmentService', () => {
      const candidate = readOne<Candidate>('candidates', id);
      if (!candidate) throw errors.notFound('candidate');
      const requisition = readOne<JobRequisition>('requisitions', candidate.requisitionId);
      return {
        candidate,
        history: [
          {
            id: `${id}:applied`,
            fromStage: null,
            toStage: 'applied',
            note: null,
            actorName: null,
            createdAt: candidate.appliedAt,
          },
        ],
        interviews: candidate.interviews.map((interview): CandidateInterviewRecord => ({
          id: interview.id,
          scheduledFor: interview.scheduledAt,
          kind: interview.kind,
          interviewerName: interview.interviewerName,
          status:
            interview.outcome === 'pending'
              ? 'scheduled'
              : interview.outcome === 'no_show'
                ? 'no_show'
                : 'completed',
          feedback: interview.notes,
          recommendation:
            interview.outcome === 'passed'
              ? 'advance'
              : interview.outcome === 'failed'
                ? 'do_not_advance'
                : null,
        })),
        allowedStages: [...STAGE_ORDER, 'rejected', 'withdrawn'].filter(
          (stage): stage is CandidateStage =>
            stage !== 'offer' &&
            stage !== 'hired' &&
            stage !== candidate.stage &&
            canMoveStage(candidate.stage, stage as CandidateStage),
        ),
        requisitionDepartmentId: requisition?.departmentId ?? null,
        requisitionPlacement: requisition
          ? {
              positionId: requisition.positionId,
              employmentType: requisition.employmentType,
              workLocationId: requisition.workLocationId,
              hiringManagerEmployeeId: requisition.hiringManagerEmployeeId,
            }
          : null,
      };
    });
  },

  async addCandidate(input: NewCandidateInput): Promise<Candidate> {
    return serviceCall('recruitmentService', async () => {
      const requisition = input.requisitionId
        ? readOne<JobRequisition>('requisitions', input.requisitionId)
        : null;
      if (input.requisitionId && requisition?.status !== 'open') {
        throw errors.precondition('That requisition has not been approved yet.');
      }
      const candidate: Candidate = {
        id: nextId('cand'),
        referenceCode: nextReference('CND', new Date().getFullYear()),
        fullName: input.fullName,
        email: input.email,
        mobileNumber: input.mobileNumber ?? '',
        requisitionId: input.requisitionId ?? '',
        requisitionTitle: requisition?.title ?? '',
        stage: 'applied',
        source: input.source ?? 'direct',
        appliedAt: runtime.now(),
        resumeFileName: null,
        expectedMonthlyRate: input.expectedMonthlyRate
          ? { minorUnits: Math.round(input.expectedMonthlyRate * 100), currency: 'PHP' }
          : null,
        yearsExperience: 0,
        interviews: [],
        offer: null,
        notes: [],
        rejectionReason: null,
        convertedEmployeeId: null,
        updatedAt: runtime.now(),
      };
      await insert('candidates', candidate);
      return candidate;
    });
  },

  async closeCandidate(
    id: string,
    outcome: 'rejected' | 'withdrawn',
    reason: string,
  ): Promise<Candidate> {
    return serviceCall('recruitmentService', async () => {
      if (!reason.trim()) throw errors.validation(`Give the reason the candidate is ${outcome}.`);
      const before = readOne<Candidate>('candidates', id);
      if (!before) throw errors.notFound('candidate');
      if (!canMoveStage(before.stage, outcome)) {
        throw errors.precondition(`A ${before.stage} candidate cannot be ${outcome}.`);
      }
      return update<Candidate>(
        'candidates',
        id,
        (row) => ({ ...row, stage: outcome, rejectionReason: reason, updatedAt: runtime.now() }),
        'candidate',
      );
    });
  },

  async extendOffer(
    id: string,
    terms: { monthlyRate: number; startDate: string },
  ): Promise<Candidate> {
    return serviceCall('recruitmentService', async () => {
      const before = readOne<Candidate>('candidates', id);
      if (!before) throw errors.notFound('candidate');
      if (
        ['applied', 'screening', 'assessment', 'hired', 'rejected', 'withdrawn'].includes(
          before.stage,
        )
      ) {
        throw errors.precondition(`A candidate cannot move from ${before.stage} to offer.`);
      }
      return update<Candidate>(
        'candidates',
        id,
        (row) => ({
          ...row,
          stage: 'offer',
          offer: {
            id: `${id}:offer`,
            monthlyRate: { minorUnits: Math.round(terms.monthlyRate * 100), currency: 'PHP' },
            startDate: terms.startDate,
            positionTitle: row.requisitionTitle,
            status: 'sent',
            sentAt: runtime.now(),
            respondedAt: null,
            notes: null,
          },
          updatedAt: runtime.now(),
        }),
        'candidate',
      );
    });
  },

  async acceptOffer(id: string): Promise<Candidate> {
    return serviceCall('recruitmentService', async () => {
      const before = readOne<Candidate>('candidates', id);
      if (!before) throw errors.notFound('candidate');
      if (before.stage !== 'offer' || before.offer?.status !== 'sent') {
        throw errors.precondition('That candidate has no open offer to accept.');
      }
      return update<Candidate>(
        'candidates',
        id,
        (row) => ({
          ...row,
          stage: 'hired',
          offer: row.offer
            ? { ...row.offer, status: 'accepted', respondedAt: runtime.now() }
            : null,
          updatedAt: runtime.now(),
        }),
        'candidate',
      );
    });
  },

  async scheduleInterview(
    candidateId: string,
    input: { scheduledFor: string; kind: string; interviewerName: string },
  ): Promise<CandidateInterviewRecord> {
    return serviceCall('recruitmentService', async () => {
      const interview = {
        id: nextId('int'),
        scheduledAt: input.scheduledFor,
        durationMinutes: 60,
        interviewerName: input.interviewerName,
        kind: 'screening' as const,
        mode: 'video' as const,
        outcome: 'pending' as const,
        notes: null,
      };
      await update<Candidate>(
        'candidates',
        candidateId,
        (row) => ({ ...row, interviews: [...row.interviews, interview] }),
        'candidate',
      );
      return {
        id: interview.id,
        scheduledFor: input.scheduledFor,
        kind: input.kind,
        interviewerName: input.interviewerName,
        status: 'scheduled',
        feedback: null,
        recommendation: null,
      };
    });
  },

  async recordInterviewOutcome(
    candidateId: string,
    interviewId: string,
    input: InterviewOutcomeInput,
  ): Promise<CandidateInterviewRecord> {
    return serviceCall('recruitmentService', async () => {
      const outcome =
        input.status === 'no_show'
          ? ('no_show' as const)
          : input.recommendation === 'do_not_advance'
            ? ('failed' as const)
            : ('passed' as const);
      const after = await update<Candidate>(
        'candidates',
        candidateId,
        (row) => ({
          ...row,
          interviews: row.interviews.map((interview) =>
            interview.id === interviewId
              ? { ...interview, outcome, notes: input.feedback }
              : interview,
          ),
        }),
        'candidate',
      );
      const interview = after.interviews.find((entry) => entry.id === interviewId);
      if (!interview) throw errors.notFound('interview');
      return {
        id: interview.id,
        scheduledFor: interview.scheduledAt,
        kind: interview.kind,
        interviewerName: interview.interviewerName,
        status: input.status,
        feedback: input.feedback,
        recommendation: input.recommendation ?? null,
      };
    });
  },

  /**
   * Convert a hired candidate into an employee.
   *
   * Creates the employee, links the candidate to it, and opens an onboarding
   * case in one operation — so the three records can never disagree about
   * whether someone was actually hired.
   */
  /**
   * Turn a hired candidate into an employee.
   *
   * Creates the employee here rather than taking one the caller made. That is
   * not a refactor for tidiness: the server does the employee, their employment
   * history, their salary and the onboarding case in one transaction, because
   * three records that disagree about whether somebody was hired is worse than
   * none of them existing. A caller that creates the employee first and then
   * asks for the conversion can fail between the two, and does so leaving a
   * person on the payroll with no onboarding and no candidate link.
   *
   * The signature therefore takes the *placement* — where this person is going
   * — and nothing that the service can work out for itself.
   */
  /** The company-pattern work address conversion would use. */
  async suggestWorkEmail(candidateId: string): Promise<string> {
    return serviceCall('recruitmentService', () => {
      const candidate = readOne<Candidate>('candidates', candidateId);
      const parts = (candidate?.fullName ?? '').trim().toLowerCase().split(/\s+/);
      const domain = requireSession().email.split('@')[1] ?? 'example.com';
      return `${[parts[0], parts.length > 1 ? parts.at(-1) : null].filter(Boolean).join('.')}@${domain}`;
    });
  },

  async convertToEmployee(
    candidateId: string,
    placement: {
      departmentId: string;
      employmentType?: string;
      positionId?: string | null;
      workLocationId?: string | null;
      payrollGroupId?: string | null;
      managerId?: string | null;
      startDate?: string;
      workEmail?: string;
    },
  ): Promise<{ employee: ConvertedEmployee; onboardingCaseId: string | null }> {
    return serviceCall('recruitmentService', async () => {
      const session = requireSession();
      const actor: AuditActor = {
        userId: session.userId,
        name: session.displayName,
        roles: session.roles,
      };

      const candidate = readOne<Candidate>('candidates', candidateId);
      if (!candidate) throw errors.notFound('candidate');
      if (candidate.stage !== 'hired' && candidate.offer?.status !== 'accepted') {
        throw errors.precondition(
          'Only a hired candidate with an accepted offer can be converted to an employee.',
        );
      }
      if (candidate.convertedEmployeeId) {
        throw errors.conflict('That candidate was already converted to an employee.');
      }

      const department = readOne<Department>('departments', placement.departmentId);
      const parts = candidate.fullName.trim().split(/\s+/);

      const employee = {
        id: nextId('emp'),
        employeeNumber: nextReference('MTC', new Date().getFullYear()),
        firstName: parts[0] ?? candidate.fullName,
        middleName: parts.length > 2 ? parts.slice(1, -1).join(' ') : '',
        lastName: parts.length > 1 ? (parts.at(-1) ?? '') : '',
        preferredName: parts[0] ?? '',
        fullName: candidate.fullName,
        workEmail: placement.workEmail ?? candidate.email,
        personalEmail: candidate.email,
        mobileNumber: candidate.mobileNumber,
        departmentId: placement.departmentId,
        departmentName: department?.name ?? '',
        positionId: placement.positionId ?? '',
        positionTitle: candidate.requisitionTitle,
        workLocationId: placement.workLocationId ?? '',
        managerEmployeeId: placement.managerId ?? null,
        employmentType: placement.employmentType ?? 'probationary',
        employmentStatus:
          (placement.employmentType ?? 'probationary') === 'probationary'
            ? 'probationary'
            : 'active',
        dateHired: placement.startDate ?? candidate.offer?.startDate ?? runtime.now().slice(0, 10),
      } as unknown as AdminEmployee;

      await insert('employees', employee);

      const employeeId = employee.id;
      const employeeName = employee.fullName;

      const updated = await update<Candidate>(
        'candidates',
        candidateId,
        (row) => ({
          ...row,
          convertedEmployeeId: employeeId,
          stage: 'hired',
          updatedAt: runtime.now(),
        }),
        'candidate',
      );

      const onboardingCase: OnboardingCase = {
        id: nextId('onb'),
        employeeId,
        employeeName,
        positionTitle: candidate.requisitionTitle,
        departmentName: '',
        managerName: null,
        startDate: placement.startDate ?? candidate.offer?.startDate ?? runtime.now().slice(0, 10),
        templateId: 'tmpl_standard',
        status: 'not_started',
        tasks: [],
        createdAt: runtime.now(),
        closedAt: null,
      };
      await insert('onboardingCases', onboardingCase);

      await recordAudit({
        actor,
        module: 'recruitment',
        action: 'candidate.converted',
        summary: `Converted ${candidate.fullName} to employee ${employeeName}`,
        recordType: 'candidate',
        recordId: candidateId,
        recordLabel: candidate.referenceCode,
        subjectEmployeeId: employeeId,
        after: { employeeId, onboardingCaseId: onboardingCase.id },
        severity: 'notice',
      });

      void updated;
      return { employee, onboardingCaseId: onboardingCase.id };
    });
  },
};

/* -------------------------------------------------------------------------- */
/* Onboarding, probation, offboarding                                          */
/* -------------------------------------------------------------------------- */

function progressOf(tasks: { status: string }[]): number {
  if (tasks.length === 0) return 0;
  return Math.round(
    (tasks.filter((task) => task.status === 'completed').length / tasks.length) * 100,
  );
}

const mockOnboardingService = {
  list: (filter: PageRequest & { status?: string }) =>
    listOf<OnboardingCase>(
      'onboardingService',
      'onboardingCases',
      filter,
      (row) => `${row.employeeName} ${row.positionTitle} ${row.departmentName}`,
      (row) => !filter.status || filter.status === 'all' || row.status === filter.status,
    ),

  progressOf,

  async completeTask(caseId: string, taskId: string, actor: AuditActor): Promise<OnboardingCase> {
    return serviceCall('onboardingService', async () => {
      const after = await update<OnboardingCase>(
        'onboardingCases',
        caseId,
        (row) => {
          const tasks = row.tasks.map((task) =>
            task.id === taskId
              ? {
                  ...task,
                  status: 'completed' as const,
                  completedAt: runtime.now(),
                  completedByName: actor.name,
                }
              : task,
          );
          const done = tasks.every((task) => task.status === 'completed');
          return {
            ...row,
            tasks,
            status: done ? ('completed' as const) : ('in_progress' as const),
            closedAt: done ? runtime.now() : row.closedAt,
          };
        },
        'onboarding case',
      );

      await recordAudit({
        actor,
        module: 'onboarding',
        action: 'onboarding.task_completed',
        summary: `Completed an onboarding task for ${after.employeeName}`,
        recordType: 'onboarding_case',
        recordId: caseId,
        recordLabel: after.employeeName,
        subjectEmployeeId: after.employeeId,
        metadata: { taskId, progress: progressOf(after.tasks) },
      });

      return after;
    });
  },
};

const mockProbationService = {
  list: (filter: PageRequest & { decision?: string }) =>
    listOf<ProbationReview>(
      'probationService',
      'probationReviews',
      filter,
      (row) => `${row.employeeName} ${row.positionTitle} ${row.departmentName}`,
      (row) => !filter.decision || filter.decision === 'all' || row.hrDecision === filter.decision,
    ),

  /**
   * Which probation reviews are running out of time.
   *
   * A probation decision not taken by its due date can convert an employee to
   * regular status by default, so "overdue" here is a legal exposure rather
   * than an untidy queue. Counted against the review due date, not the
   * probation end date — the review has to happen first.
   */
  async summary() {
    return serviceCall('probationService', () => {
      const rows = readAll<ProbationReview>('probationReviews');
      // An extended probation is decided again when its review comes round.
      const pending = rows.filter(
        (row) => row.hrDecision === 'pending' || row.hrDecision === 'extend',
      );
      const today = runtime.now().slice(0, 10);
      const inFortnight = new Date(new Date(runtime.now()).getTime() + 14 * 86_400_000)
        .toISOString()
        .slice(0, 10);

      return {
        // The prototype has no employment status on a review; an undecided
        // review is the nearest it has to "still on probation".
        onProbation: new Set(pending.map((row) => row.employeeId)).size,
        // The decision window — overdue or due within the fortnight — as the API's summary counts it.
        pending: pending.filter((row) => row.reviewDueDate <= inFortnight).length,
        overdue: pending.filter((row) => row.reviewDueDate < today).length,
        dueSoon: pending.filter(
          (row) => row.reviewDueDate >= today && row.reviewDueDate <= inFortnight,
        ).length,
        startingLater: 0,
        decided: rows.filter((row) =>
          ['confirm_employment', 'extend', 'end_employment'].includes(row.hrDecision),
        ).length,
      };
    });
  },

  async decide(
    id: string,
    decision: ProbationReview['hrDecision'],
    comment: string,
    actor: AuditActor,
    options: { probationEndDate?: string } = {},
  ): Promise<ProbationReview> {
    return serviceCall('probationService', async () => {
      if (decision === 'pending') throw errors.validation('Choose a decision.');
      if (!comment.trim()) {
        throw errors.validation('A comment is required — this decision affects employment.');
      }

      const after = await update<ProbationReview>(
        'probationReviews',
        id,
        (row) => {
          // An extension moves the end of probation and the review with it, as the API does.
          const newEnd =
            decision === 'extend'
              ? (options.probationEndDate ?? addMonthsToKey(row.probationEndDate, 3))
              : null;
          if (newEnd) {
            const problem = extensionError(
              newEnd,
              row.probationEndDate,
              runtime.now().slice(0, 10),
              (key) => key,
            );
            if (problem) throw errors.validation(problem);
          }
          return {
            ...row,
            hrDecision: decision,
            hrComment: comment,
            decidedAt: runtime.now(),
            decidedByUserId: actor.userId,
            ...(newEnd ? { probationEndDate: newEnd, reviewDueDate: reviewDueFor(newEnd) } : {}),
          };
        },
        'probation review',
      );

      await recordAudit({
        actor,
        module: 'probation',
        action: 'probation.decided',
        summary: `${decision.replace(/_/g, ' ')} for ${after.employeeName}`,
        recordType: 'probation_review',
        recordId: id,
        recordLabel: after.employeeName,
        subjectEmployeeId: after.employeeId,
        after: { decision },
        metadata: { comment },
        severity: 'critical',
      });

      return after;
    });
  },
};

const mockOffboardingService = {
  list: (filter: PageRequest & { status?: string }) =>
    listOf<OffboardingCase>(
      'offboardingService',
      'offboardingCases',
      filter,
      (row) => `${row.employeeName} ${row.referenceCode} ${row.departmentName}`,
      (row) => !filter.status || filter.status === 'all' || row.status === filter.status,
    ),

  progressOf: (row: OffboardingCase) => progressOf(row.clearanceTasks),

  /** The current workforce, less anybody already in an open case. */
  async eligibleEmployees(): Promise<OffboardingCandidate[]> {
    return serviceCall('offboardingService', () => {
      const open = new Set(
        readAll<OffboardingCase>('offboardingCases')
          .filter((row) => row.status !== 'completed' && row.status !== 'cancelled')
          .map((row) => row.employeeId),
      );
      return readAll<AdminEmployee>('employees')
        .filter(
          (employee) =>
            !open.has(employee.id) &&
            employee.employmentStatus !== 'inactive' &&
            employee.employmentStatus !== 'resigned',
        )
        .sort((a, b) => a.fullName.localeCompare(b.fullName))
        .map((employee) => ({
          id: employee.id,
          fullName: employee.fullName,
          employeeNumber: employee.employeeNumber,
          departmentName: employee.departmentName,
          positionTitle: employee.positionTitle,
        }));
    });
  },

  async open(input: OffboardingInput, actor: AuditActor): Promise<OffboardingCase> {
    return serviceCall('offboardingService', async () => {
      const employee = readOne<AdminEmployee>('employees', input.employeeId);
      if (!employee) throw errors.notFound('employee');
      const titles: [string, WorkflowTask['ownerKind'], boolean][] = [
        ['Return company ID and access card', 'admin', true],
        ['Return laptop and accessories', 'it', true],
        ['Revoke system access', 'it', true],
        ['Settle outstanding loans', 'finance', true],
        ['Exit interview', 'hr', false],
      ];
      const created: OffboardingCase = {
        id: `off_${Date.now()}`,
        referenceCode: nextReference('SEP', Number(input.lastWorkingDay.slice(0, 4))),
        employeeId: employee.id,
        employeeName: employee.fullName,
        positionTitle: employee.positionTitle,
        departmentName: employee.departmentName,
        managerName: employee.managerName,
        reason: input.reason,
        reasonLabel: offboardingReasonLabel(input.reason),
        notes: input.notes?.trim() || null,
        noticeDate: input.noticeDate,
        lastWorkingDay: input.lastWorkingDay,
        // Nothing done yet: "Not started", as the API opens it.
        status: 'not_started',
        clearanceTasks: titles.map(([title, ownerKind, isBlocking], index) => ({
          id: `clr_${Date.now()}_${index}`,
          title,
          description: null,
          ownerKind,
          ownerName: null,
          isBlocking,
          dueDate: input.lastWorkingDay,
          status: 'not_started',
          completedAt: null,
          completedByName: null,
          notes: null,
        })),
        assetsOutstanding: 0,
        loanBalance: null,
        leaveBalanceDays: null,
        accountDisabled: false,
        exitInterviewCompleted: false,
        exitInterviewNotes: null,
        finalPayStatus: 'not_started',
        createdAt: runtime.now(),
        closedAt: null,
      };
      await insert('offboardingCases', created);
      await recordAudit({
        actor,
        module: 'offboarding',
        action: 'offboarding.case_opened',
        summary: `Opened offboarding case ${created.referenceCode} for ${created.employeeName}`,
        recordType: 'offboarding_case',
        recordId: created.id,
        recordLabel: created.referenceCode,
        subjectEmployeeId: created.employeeId,
      });
      return created;
    });
  },

  async completeClearanceTask(
    caseId: string,
    taskId: string,
    actor: AuditActor,
  ): Promise<OffboardingCase> {
    return serviceCall('offboardingService', async () => {
      const after = await update<OffboardingCase>(
        'offboardingCases',
        caseId,
        (row) => ({
          ...row,
          clearanceTasks: row.clearanceTasks.map((task) =>
            task.id === taskId
              ? {
                  ...task,
                  status: 'completed' as const,
                  completedAt: runtime.now(),
                  completedByName: actor.name,
                }
              : task,
          ),
        }),
        'offboarding case',
      );
      await recordAudit({
        actor,
        module: 'offboarding',
        action: 'offboarding.clearance_task_completed',
        summary: `Completed a clearance task for ${after.employeeName}`,
        recordType: 'offboarding_case',
        recordId: caseId,
        recordLabel: after.referenceCode,
        subjectEmployeeId: after.employeeId,
      });
      return after;
    });
  },

  async close(caseId: string, actor: AuditActor): Promise<OffboardingCase> {
    return serviceCall('offboardingService', async () => {
      const before = readOne<OffboardingCase>('offboardingCases', caseId);
      if (!before) throw errors.notFound('offboarding case');

      const outstanding = before.clearanceTasks.filter((task) => task.status !== 'completed');
      if (outstanding.length > 0) {
        throw errors.precondition(
          `${outstanding.length === 1 ? '1 clearance task is' : `${outstanding.length} clearance tasks are`} still open. A case cannot close with outstanding clearance.`,
        );
      }

      const after = await update<OffboardingCase>(
        'offboardingCases',
        caseId,
        (row) => ({
          ...row,
          status: 'completed',
          closedAt: runtime.now(),
          finalPayStatus: 'ready_for_payroll',
        }),
        'offboarding case',
      );

      await recordAudit({
        actor,
        module: 'offboarding',
        action: 'offboarding.closed',
        summary: `Completed offboarding case ${after.referenceCode} for ${after.employeeName}`,
        recordType: 'offboarding_case',
        recordId: caseId,
        recordLabel: after.referenceCode,
        subjectEmployeeId: after.employeeId,
        severity: 'sensitive',
      });

      return after;
    });
  },
};

/* -------------------------------------------------------------------------- */
/* Assets                                                                      */
/* -------------------------------------------------------------------------- */

const mockAssetService = {
  list: (filter: PageRequest & { status?: string; category?: string }) =>
    listOf<Asset>(
      'assetService',
      'assets',
      filter,
      (row) => `${row.assetCode} ${row.name} ${row.serialNumber} ${row.assignedEmployeeName ?? ''}`,
      (row) =>
        (!filter.status || filter.status === 'all' || row.status === filter.status) &&
        (!filter.category || filter.category === 'all' || row.category === filter.category),
    ),

  async summary() {
    return serviceCall('assetService', () => {
      const rows = readAll<Asset>('assets');
      return {
        total: rows.length,
        assigned: rows.filter((row) => row.status === 'assigned').length,
        available: rows.filter((row) => row.status === 'available').length,
        maintenance: rows.filter((row) => row.status === 'maintenance').length,
        lostOrDamaged: rows.filter((row) => row.status === 'lost' || row.status === 'damaged')
          .length,
        retired: rows.filter((row) => row.status === 'retired').length,
      };
    });
  },

  async assign(
    id: string,
    employeeId: string,
    employeeName: string,
    actor: AuditActor,
  ): Promise<Asset> {
    return serviceCall('assetService', async () => {
      const before = readOne<Asset>('assets', id);
      if (!before) throw errors.notFound('asset');
      if (before.status === 'assigned') {
        throw errors.precondition(
          `${before.assetCode} is already assigned to ${before.assignedEmployeeName}. Record a return first.`,
        );
      }
      if (before.status === 'retired' || before.status === 'lost') {
        throw errors.precondition(
          `${before.assetCode} is ${before.status} and cannot be assigned.`,
        );
      }

      const today = runtime.now().slice(0, 10);
      const after = await update<Asset>(
        'assets',
        id,
        (row) => ({
          ...row,
          status: 'assigned',
          assignedEmployeeId: employeeId,
          assignedEmployeeName: employeeName,
          assignedOn: today,
          history: [
            ...row.history,
            {
              id: nextId('hist'),
              employeeId,
              employeeName,
              assignedOn: today,
              returnedOn: null,
              conditionOnReturn: null,
              notes: null,
            },
          ],
        }),
        'asset',
      );

      await recordAudit({
        actor,
        module: 'assets',
        action: 'asset.assigned',
        summary: `Assigned ${after.assetCode} to ${employeeName}`,
        recordType: 'asset',
        recordId: id,
        recordLabel: after.assetCode,
        subjectEmployeeId: employeeId,
        before: { status: before.status },
        after: { status: 'assigned', employeeId },
      });

      return after;
    });
  },

  async recordReturn(
    id: string,
    condition: Asset['condition'],
    notes: string,
    actor: AuditActor,
  ): Promise<Asset> {
    return serviceCall('assetService', async () => {
      const before = readOne<Asset>('assets', id);
      if (!before) throw errors.notFound('asset');
      if (before.status !== 'assigned') {
        throw errors.precondition(`${before.assetCode} is not currently assigned.`);
      }

      const today = runtime.now().slice(0, 10);
      const after = await update<Asset>(
        'assets',
        id,
        (row) => ({
          ...row,
          status: condition === 'poor' ? 'maintenance' : 'available',
          condition,
          assignedEmployeeId: null,
          assignedEmployeeName: null,
          assignedOn: null,
          history: row.history.map((entry, index) =>
            index === row.history.length - 1
              ? { ...entry, returnedOn: today, conditionOnReturn: condition, notes: notes || null }
              : entry,
          ),
        }),
        'asset',
      );

      await recordAudit({
        actor,
        module: 'assets',
        action: 'asset.returned',
        summary: `${before.assignedEmployeeName} returned ${after.assetCode} in ${condition} condition`,
        recordType: 'asset',
        recordId: id,
        recordLabel: after.assetCode,
        subjectEmployeeId: before.assignedEmployeeId,
        metadata: { condition, notes },
      });

      return after;
    });
  },

  async setStatus(
    id: string,
    status: Asset['status'],
    reason: string,
    actor: AuditActor,
  ): Promise<Asset> {
    return serviceCall('assetService', async () => {
      if ((status === 'lost' || status === 'damaged' || status === 'retired') && !reason.trim()) {
        throw errors.validation('A reason is required for this status.');
      }
      const after = await update<Asset>('assets', id, (row) => ({ ...row, status }), 'asset');
      await recordAudit({
        actor,
        module: 'assets',
        action: `asset.${status}`,
        summary: `Marked ${after.assetCode} as ${status}`,
        recordType: 'asset',
        recordId: id,
        recordLabel: after.assetCode,
        metadata: { reason },
        severity: status === 'lost' ? 'notice' : 'info',
      });
      return after;
    });
  },
};

/* -------------------------------------------------------------------------- */
/* Employee relations                                                          */
/* -------------------------------------------------------------------------- */

const mockEmployeeRelationsService = {
  /**
   * Cases are permission-checked on *read*, not only on navigation.
   *
   * This is the most confidential record in the platform: a manager must never
   * be able to reach a case about their own report through a shared link.
   */
  async list(
    filter: PageRequest & { status?: string },
    permissions: string[],
  ): Promise<Page<EmployeeRelationsCase>> {
    if (!permissions.includes('employee_relations.view')) {
      throw errors.permissionDenied('employee_relations.view');
    }
    return listOf<EmployeeRelationsCase>(
      'employeeRelationsService',
      'relationsCases',
      filter,
      (row) => `${row.employeeName} ${row.referenceCode} ${row.subject}`,
      (row) => !filter.status || filter.status === 'all' || row.status === filter.status,
    );
  },

  async get(id: string, permissions: string[]): Promise<EmployeeRelationsCase | null> {
    if (!permissions.includes('employee_relations.view')) {
      throw errors.permissionDenied('employee_relations.view');
    }
    return serviceCall('employeeRelationsService', () =>
      readOne<EmployeeRelationsCase>('relationsCases', id),
    );
  },

  async addTimelineEntry(
    id: string,
    kind: EmployeeRelationsCase['timeline'][number]['kind'],
    body: string,
    actor: AuditActor,
    permissions: string[],
  ): Promise<EmployeeRelationsCase> {
    if (!permissions.includes('employee_relations.edit')) {
      throw errors.permissionDenied('employee_relations.edit');
    }
    return serviceCall('employeeRelationsService', async () => {
      if (!body.trim()) throw errors.validation('An entry cannot be empty.');
      const after = await update<EmployeeRelationsCase>(
        'relationsCases',
        id,
        (row) => ({
          ...row,
          timeline: [
            ...row.timeline,
            { id: nextId('tl'), at: runtime.now(), actorName: actor.name, kind, body },
          ],
        }),
        'case',
      );
      await recordAudit({
        actor,
        module: 'employee_relations',
        action: 'case.entry_added',
        summary: `Added a ${kind} entry to case ${after.referenceCode}`,
        recordType: 'employee_relations_case',
        recordId: id,
        recordLabel: after.referenceCode,
        subjectEmployeeId: after.employeeId,
        severity: 'sensitive',
      });
      return after;
    });
  },
};

/* -------------------------------------------------------------------------- */
/* Compensation, benefits, loans                                               */
/* -------------------------------------------------------------------------- */

/*
 * Compensation.
 *
 * The signature deliberately no longer takes `permissions` or an `AuditActor`.
 * Both were the prototype asking the caller for things a server already knows:
 * who is calling, and what they may do. Passing them from a component means
 * every call site is one forgotten argument away from an unenforced check —
 * and the API's version cannot accept them at all, because it resolves both
 * from the session.
 *
 * The mock reads the current session for the same information, so the two
 * adapters share one shape and the screens stopped carrying authorisation
 * arguments they should never have held.
 */
const mockCompensationService = {
  async forEmployee(employeeId: string): Promise<CompensationRecord[]> {
    const session = requireSession();
    if (!session.permissions.includes('compensation.view')) {
      throw errors.permissionDenied('compensation.view');
    }
    return serviceCall('compensationService', () =>
      readAll<CompensationRecord>('compensation')
        .filter((row) => row.employeeId === employeeId)
        .sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate)),
    );
  },

  async overview(employeeId: string): Promise<CompensationOverview> {
    const history = await mockCompensationService.forEmployee(employeeId);
    // The business calendar's today, not UTC's — which in Manila is still
    // yesterday until 08:00, and made a rate starting today "not in effect yet".
    const today = todayInBusiness(new Date(runtime.now()));
    const approved = history.filter((row) => row.approvalStatus === 'approved');
    return {
      history,
      current: approved.find((row) => row.effectiveDate <= today) ?? null,
      upcoming: approved.filter((row) => row.effectiveDate > today).reverse(),
      requiresApproval: true,
    };
  },

  async decide(
    changeId: string,
    decision: 'approve' | 'reject',
    note: string,
  ): Promise<CompensationRecord> {
    return serviceCall('compensationService', async () => {
      const session = requireSession();
      const before = readOne<CompensationRecord>('compensation', changeId);
      if (!before) throw errors.notFound('compensation change');
      if (before.createdByUserId === session.userId) {
        throw errors.precondition('A change cannot be decided by the person who recorded it.');
      }
      return update<CompensationRecord>('compensation', changeId, (row) => ({
        ...row,
        approvalStatus: decision === 'approve' ? 'approved' : 'rejected',
        approvedByUserId: session.userId,
        approvedAt: runtime.now(),
        decisionNote: note || null,
      }));
    });
  },

  async record(
    employeeId: string,
    input: Omit<
      CompensationRecord,
      | 'id'
      | 'employeeId'
      | 'createdAt'
      | 'createdByUserId'
      | 'approvalStatus'
      | 'approvedAt'
      | 'approvedByUserId'
    >,
  ): Promise<CompensationRecord> {
    const session = requireSession();
    const actor: AuditActor = {
      userId: session.userId,
      name: session.displayName,
      roles: session.roles,
    };

    if (!session.permissions.includes('compensation.manage')) {
      throw errors.permissionDenied('compensation.manage');
    }
    return serviceCall('compensationService', async () => {
      if (!input.reason.trim()) {
        throw errors.validation('A compensation change requires a reason.');
      }
      const existing = readAll<CompensationRecord>('compensation').filter(
        (row) => row.employeeId === employeeId,
      );
      if (existing.some((row) => row.effectiveDate === input.effectiveDate)) {
        throw errors.conflict(
          'This employee already has a compensation record with that effective date.',
        );
      }

      const record: CompensationRecord = {
        ...input,
        employeeId,
        id: nextId('cmp'),
        approvalStatus: 'pending_hr',
        approvedByUserId: null,
        approvedAt: null,
        createdAt: runtime.now(),
        createdByUserId: actor.userId,
      };
      await insert('compensation', record);

      await recordAudit({
        actor,
        module: 'compensation',
        action: 'compensation.recorded',
        summary: `Recorded a ${input.changeType.replace(/_/g, ' ')} effective ${input.effectiveDate}`,
        recordType: 'compensation_record',
        recordId: record.id,
        recordLabel: input.effectiveDate,
        subjectEmployeeId: employeeId,
        before: { monthlyRate: input.previousMonthlyRate },
        after: { monthlyRate: input.monthlyRate },
        metadata: { reason: input.reason },
        severity: 'sensitive',
      });

      return record;
    });
  },
};

/** An enrolment as the benefits screen lists it: with the person's name. */
export interface BenefitEnrollmentRow extends BenefitEnrollment {
  employeeName: string | null;
  employeeNumber: string | null;
}

const mockBenefitsService = {
  async enrollments(filter: { planId?: string } = {}): Promise<BenefitEnrollmentRow[]> {
    return serviceCall('benefitsService', () => {
      const employees = readAll<{ id: string; fullName: string; employeeNumber: string }>(
        'employees',
      );
      return readAll<BenefitEnrollment>('benefitEnrollments')
        .filter((row) => !filter.planId || row.planId === filter.planId)
        .map((row) => {
          const employee = employees.find((entry) => entry.id === row.employeeId);
          return {
            ...row,
            employeeName: employee?.fullName ?? null,
            employeeNumber: employee?.employeeNumber ?? null,
          };
        })
        .sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));
    });
  },

  plans: (filter: PageRequest) =>
    listOf<BenefitPlan>(
      'benefitsService',
      'benefitPlans',
      filter,
      (row) => `${row.name} ${row.category}`,
    ),

  async enrollmentsFor(employeeId: string): Promise<BenefitEnrollment[]> {
    return serviceCall('benefitsService', () =>
      readAll<BenefitEnrollment>('benefitEnrollments').filter(
        (row) => row.employeeId === employeeId,
      ),
    );
  },

  async enroll(
    planId: string,
    employeeId: string,
    effectiveDate: string,
    actor: AuditActor,
  ): Promise<BenefitEnrollment> {
    return serviceCall('benefitsService', async () => {
      const plan = readOne<BenefitPlan>('benefitPlans', planId);
      if (!plan) throw errors.notFound('benefit plan');

      const existing = readAll<BenefitEnrollment>('benefitEnrollments').find(
        (row) => row.planId === planId && row.employeeId === employeeId && row.status === 'active',
      );
      if (existing) throw errors.conflict(`This employee is already enrolled in ${plan.name}.`);

      const enrollment: BenefitEnrollment = {
        id: nextId('enr'),
        planId,
        planName: plan.name,
        employeeId,
        status: 'active',
        effectiveDate,
        endDate: null,
        dependentNames: [],
        documentName: null,
        notes: null,
        createdAt: runtime.now(),
      };
      await insert('benefitEnrollments', enrollment);

      await recordAudit({
        actor,
        module: 'benefits',
        action: 'benefit.enrolled',
        summary: `Enrolled an employee in ${plan.name}`,
        recordType: 'benefit_enrollment',
        recordId: enrollment.id,
        recordLabel: plan.name,
        subjectEmployeeId: employeeId,
      });

      return enrollment;
    });
  },
};

const mockLoanService = {
  list: (filter: PageRequest & { status?: string }) =>
    listOf<EmployeeLoan>(
      'loanService',
      'loans',
      filter,
      (row) => `${row.referenceCode} ${row.loanType}`,
      (row) => !filter.status || filter.status === 'all' || row.status === filter.status,
    ),

  async forEmployee(employeeId: string): Promise<EmployeeLoan[]> {
    return serviceCall('loanService', () =>
      readAll<EmployeeLoan>('loans').filter((row) => row.employeeId === employeeId),
    );
  },

  /**
   * What the company is still owed.
   *
   * Outstanding is derived from `totalPayable - amountPaid` on every active
   * loan rather than stored, so it cannot drift from the payment history that
   * produced it. Minor units throughout — the arithmetic never touches a float.
   */
  async summary() {
    return serviceCall('loanService', () => {
      const rows = readAll<EmployeeLoan>('loans');
      const active = rows.filter((row) => row.status === 'active');
      const outstanding = active.reduce(
        (total, row) => total + (row.totalPayable.minorUnits - row.amountPaid.minorUnits),
        0,
      );

      return {
        active: active.length,
        completed: rows.filter((row) => row.status === 'completed').length,
        outstanding: { minorUnits: outstanding, currency: 'PHP' as const },
        notInPayroll: active.filter((row) => !row.payrollIntegrated).length,
      };
    });
  },
};

/* -------------------------------------------------------------------------- */
/* Performance and learning                                                    */
/* -------------------------------------------------------------------------- */

const mockPerformanceService = {
  async cycles(): Promise<ReviewCycle[]> {
    return serviceCall('performanceService', () => readAll<ReviewCycle>('reviewCycles'));
  },

  reviews: (filter: PageRequest & { cycleId?: string; status?: string }) =>
    listOf<PerformanceReview>(
      'performanceService',
      'performanceReviews',
      filter,
      (row) => `${row.employeeName} ${row.positionTitle} ${row.cycleName}`,
      (row) =>
        (!filter.cycleId || row.cycleId === filter.cycleId) &&
        (!filter.status || filter.status === 'all' || row.status === filter.status),
    ),

  async finalize(
    id: string,
    rating: number,
    comment: string,
    actor: AuditActor,
  ): Promise<PerformanceReview> {
    return serviceCall('performanceService', async () => {
      if (rating < 1 || rating > 5)
        throw errors.validation('A final rating must be between 1 and 5.');
      if (!comment.trim())
        throw errors.validation('A manager comment is required to finalise a review.');

      const after = await update<PerformanceReview>(
        'performanceReviews',
        id,
        (row) => ({
          ...row,
          status: 'finalized',
          finalRating: rating,
          managerComment: comment,
          updatedAt: runtime.now(),
        }),
        'performance review',
      );

      await recordAudit({
        actor,
        module: 'performance',
        action: 'review.finalized',
        summary: `Finalised the review for ${after.employeeName} at ${rating}/5`,
        recordType: 'performance_review',
        recordId: id,
        recordLabel: after.employeeName,
        subjectEmployeeId: after.employeeId,
        after: { finalRating: rating },
        severity: 'sensitive',
      });

      return after;
    });
  },
};

const mockLearningService = {
  courses: (filter: PageRequest) =>
    listOf<Course>(
      'learningService',
      'courses',
      filter,
      (row) => `${row.code} ${row.title} ${row.category}`,
    ),

  sessions: (filter: PageRequest) =>
    listOf<TrainingSession>(
      'learningService',
      'trainingSessions',
      filter,
      (row) => `${row.courseTitle} ${row.facilitator} ${row.location}`,
    ),

  records: (filter: PageRequest & { status?: string; courseId?: string }) =>
    listOf<TrainingRecord>(
      'learningService',
      'trainingRecords',
      filter,
      (row) => `${row.employeeName} ${row.courseTitle}`,
      (row) =>
        (!filter.status || filter.status === 'all' || row.status === filter.status) &&
        (!filter.courseId || row.courseId === filter.courseId),
    ),

  async summary() {
    return serviceCall('learningService', () => {
      const records = readAll<TrainingRecord>('trainingRecords');
      const soon = Date.now() + 60 * 86_400_000;
      return {
        enrolled: records.filter((row) => row.status === 'enrolled').length,
        completed: records.filter((row) => row.status === 'completed').length,
        expiringSoon: records.filter(
          (row) => row.expiresOn !== null && new Date(row.expiresOn).getTime() <= soon,
        ).length,
        mandatoryOutstanding: records.filter((row) => row.status !== 'completed').length,
      };
    });
  },

  async markComplete(id: string, score: number | null, actor: AuditActor): Promise<TrainingRecord> {
    return serviceCall('learningService', async () => {
      const after = await update<TrainingRecord>(
        'trainingRecords',
        id,
        (row) => ({
          ...row,
          status: 'completed',
          completedOn: runtime.now().slice(0, 10),
          score,
        }),
        'training record',
      );
      await recordAudit({
        actor,
        module: 'learning',
        action: 'training.completed',
        summary: `Recorded completion of ${after.courseTitle} for ${after.employeeName}`,
        recordType: 'training_record',
        recordId: id,
        recordLabel: after.courseTitle,
        subjectEmployeeId: after.employeeId,
      });
      return after;
    });
  },
};

/* -------------------------------------------------------------------------- */
/* Leave configuration and balances                                            */
/* -------------------------------------------------------------------------- */

export interface LeaveType {
  id: string;
  name: string;
  shortName: string;
  paid: boolean;
  defaultCredits: number;
  requiresAttachment: boolean;
  active: boolean;
}

export interface LeaveBalanceRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  leaveTypeId: string;
  leaveTypeName: string;
  openingBalance: number;
  used: number;
  /** Days in requests still awaiting a decision; held out of `balance`. API only. */
  pending?: number;
  adjustments: number;
  balance: number;
  /**
   * Adjustments dated after today: not in `balance` or `adjustments` yet,
   * shown as upcoming. `scheduledFrom` is the earliest of their dates. API only.
   */
  scheduled?: number;
  scheduledFrom?: string | null;
  history: {
    id: string;
    at: string;
    actorName: string;
    days: number;
    reason: string;
    effectiveDate: string;
    /*
     * What kind of ledger entry: a manual adjustment, a grant, leave taken,
     * a cancellation. Absent in the prototype, which keeps adjustments only.
     */
    kind?: string;
  }[];
}

const mockLeaveConfigService = {
  async types(): Promise<LeaveType[]> {
    return serviceCall('leaveConfigService', () => readAll<LeaveType>('leaveTypes'));
  },

  async saveType(input: LeaveType & { id?: string }, actor: AuditActor): Promise<LeaveType> {
    return serviceCall('leaveConfigService', async () => {
      if (!input.name.trim()) throw errors.validation('A leave type needs a name.');
      if (input.defaultCredits < 0) throw errors.validation('Credits cannot be negative.');

      const existing = input.id ? readOne<LeaveType>('leaveTypes', input.id) : null;

      if (existing) {
        const after = await update<LeaveType>(
          'leaveTypes',
          existing.id,
          (row) => ({ ...row, ...input }),
          'leave type',
        );
        await recordAudit({
          actor,
          module: 'leave',
          action: 'leave_type.updated',
          summary: `Updated the ${after.name} leave type`,
          recordType: 'leave_type',
          recordId: after.id,
          recordLabel: after.name,
          before: { ...existing },
          after: { ...after },
          severity: 'sensitive',
        });
        return after;
      }

      const created: LeaveType = { ...input, id: nextId('lt') };
      await insert('leaveTypes', created);
      await recordAudit({
        actor,
        module: 'leave',
        action: 'leave_type.created',
        summary: `Created the ${created.name} leave type`,
        recordType: 'leave_type',
        recordId: created.id,
        recordLabel: created.name,
        after: { ...created },
        severity: 'sensitive',
      });
      return created;
    });
  },

  /**
   * Employee balances, computed from approved leave.
   *
   * Storing a balance and a list of requests means two numbers that can
   * disagree, and the one people notice is always the wrong one. The balance is
   * derived; only manual adjustments are stored.
   */
  async balances(
    filter: PageRequest & { leaveTypeId?: string },
  ): Promise<Page<LeaveBalanceRecord>> {
    return serviceCall('leaveConfigService', () => {
      const types = readAll<LeaveType>('leaveTypes');
      const employees = readAll<{ id: string; fullName: string; employmentStatus: string }>(
        'employees',
      ).filter(
        (row) => row.employmentStatus === 'active' || row.employmentStatus === 'probationary',
      );
      const requests = readAll<{
        employeeId: string;
        leaveTypeId: string;
        totalDays: number;
        status: string;
      }>('leaveRequests');
      const stored = readAll<LeaveBalanceRecord>('leaveBalances');

      const rows: LeaveBalanceRecord[] = [];
      for (const employee of employees) {
        for (const type of types) {
          if (!type.active || type.defaultCredits === 0) continue;
          if (filter.leaveTypeId && type.id !== filter.leaveTypeId) continue;

          const used = requests
            .filter(
              (request) =>
                request.employeeId === employee.id &&
                request.leaveTypeId === type.id &&
                request.status === 'approved',
            )
            .reduce((total, request) => total + request.totalDays, 0);

          const existing = stored.find(
            (row) => row.employeeId === employee.id && row.leaveTypeId === type.id,
          );
          const adjustments = existing?.adjustments ?? 0;

          rows.push({
            id: existing?.id ?? `${employee.id}:${type.id}`,
            employeeId: employee.id,
            employeeName: employee.fullName,
            leaveTypeId: type.id,
            leaveTypeName: type.name,
            openingBalance: type.defaultCredits,
            used,
            adjustments,
            balance: type.defaultCredits + adjustments - used,
            history: existing?.history ?? [],
          });
        }
      }

      return paginate(
        rows as unknown as Record<string, unknown>[],
        filter,
        (row) => `${row.employeeName} ${row.leaveTypeName}`,
      ) as unknown as Page<LeaveBalanceRecord>;
    });
  },

  /** The adjustments already made to a balance. The prototype keeps them on the row. */
  async history(record: LeaveBalanceRecord): Promise<LeaveBalanceRecord['history']> {
    return record.history;
  },

  /**
   * Adjust a balance by hand.
   *
   * Always additive and always with a reason. Setting a balance directly would
   * erase the arithmetic that produced it, and an employee asking "why do I
   * have eleven days" deserves an answer better than "because someone typed
   * eleven".
   */
  async adjustBalance(
    input: {
      employeeId: string;
      leaveTypeId: string;
      days: number;
      reason: string;
      effectiveDate: string;
      /** For the answer only; the API's ledger replies with figures, not names. */
      employeeName?: string;
      leaveTypeName?: string;
    },
    actor: AuditActor,
  ): Promise<LeaveBalanceRecord> {
    return serviceCall('leaveConfigService', async () => {
      if (!input.reason.trim()) {
        throw errors.validation('A balance adjustment requires a reason.');
      }
      if (input.days === 0) {
        throw errors.validation('An adjustment of zero days changes nothing.');
      }

      const employee = readOne<{ id: string; fullName: string }>('employees', input.employeeId);
      if (!employee) throw errors.notFound('employee');
      const type = readOne<LeaveType>('leaveTypes', input.leaveTypeId);
      if (!type) throw errors.notFound('leave type');

      const stored = readAll<LeaveBalanceRecord>('leaveBalances');
      const key = `${input.employeeId}:${input.leaveTypeId}`;
      const existing = stored.find((row) => row.id === key);

      const entry = {
        id: nextId('adj'),
        at: runtime.now(),
        actorName: actor.name,
        days: input.days,
        reason: input.reason,
        effectiveDate: input.effectiveDate,
      };

      const next: LeaveBalanceRecord = existing
        ? {
            ...existing,
            adjustments: existing.adjustments + input.days,
            history: [...existing.history, entry],
          }
        : {
            id: key,
            employeeId: input.employeeId,
            employeeName: employee.fullName,
            leaveTypeId: input.leaveTypeId,
            leaveTypeName: type.name,
            openingBalance: type.defaultCredits,
            used: 0,
            adjustments: input.days,
            balance: type.defaultCredits + input.days,
            history: [entry],
          };

      await writeAll(
        'leaveBalances',
        existing ? stored.map((row) => (row.id === key ? next : row)) : [...stored, next],
      );

      await recordAudit({
        actor,
        module: 'leave',
        action: 'leave.balance_adjusted',
        summary: `${input.days > 0 ? 'Added' : 'Removed'} ${Math.abs(input.days)} ${Math.abs(input.days) === 1 ? 'day' : 'days'} of ${type.name} for ${employee.fullName}`,
        recordType: 'leave_balance',
        recordId: key,
        recordLabel: `${employee.fullName} — ${type.name}`,
        subjectEmployeeId: input.employeeId,
        before: { adjustments: existing?.adjustments ?? 0 },
        after: { adjustments: next.adjustments },
        metadata: { reason: input.reason, effectiveDate: input.effectiveDate },
        severity: 'sensitive',
      });

      return next;
    });
  },
};

/* -------------------------------------------------------------------------- */
/* Roles and settings                                                          */
/* -------------------------------------------------------------------------- */

const SUPER_ADMINISTRATOR_LOCK =
  'The Super Administrator role cannot be edited. It holds every permission and is the role that recovers every other permission mistake.';

const mockRolePermissionService = {
  async list(): Promise<RoleDefinition[]> {
    return serviceCall('rolePermissionService', () =>
      readAll<RoleDefinition>('roles').map((role) => ({
        ...role,
        // The same lock `setPermissions` below enforces, stated on the role.
        lockedReason: role.key === 'super_administrator' ? SUPER_ADMINISTRATOR_LOCK : null,
      })),
    );
  },

  /*
   * The prototype's catalog is the web package's own groups — the mock stores
   * those keys, so there each checkbox is still exactly one stored key.
   */
  async catalog(): Promise<PermissionCatalogGroup[]> {
    return PERMISSION_GROUPS.map((group) => ({
      key: group.key,
      label: group.label,
      description: group.description,
      permissions: group.permissions.map((permission) => ({
        key: permission.key,
        label: permission.label,
        description: permission.description,
        sensitive: permission.sensitive ?? false,
        implies: [],
      })),
    }));
  },

  /** The permission the prototype's session carries for role administration. */
  editPermission: 'system.administer',

  async get(id: string): Promise<RoleDefinition | null> {
    return serviceCall('rolePermissionService', () => readOne<RoleDefinition>('roles', id));
  },

  /** How many accounts a role change would affect. Shown before saving (§40). */
  async impactOf(roleKey: string): Promise<number> {
    return serviceCall(
      'rolePermissionService',
      () =>
        readAll<{ roles: string[]; customRoleIds: string[] }>('systemUsers').filter(
          (user) => user.roles.includes(roleKey) || user.customRoleIds.includes(roleKey),
        ).length,
    );
  },

  // Mock only: the API has no create-role route (no POST /roles or
  // /roles/:id/clone) — a cloned role would exist in a browser and nowhere else.
  async clone(id: string, name: string): Promise<RoleDefinition> {
    return serviceCall('rolePermissionService', async () => {
      const session = requireSession();
      const actor: AuditActor = {
        userId: session.userId,
        name: session.displayName,
        roles: session.roles,
      };
      const source = readOne<RoleDefinition>('roles', id);
      if (!source) throw errors.notFound('role');
      if (!name.trim()) throw errors.validation('A role needs a name.');

      const role: RoleDefinition = {
        ...source,
        id: nextId('role'),
        key: name.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
        name,
        system: false,
        clonedFromRoleId: id,
        createdAt: runtime.now(),
        updatedAt: runtime.now(),
      };
      await insert('roles', role);

      await recordAudit({
        actor,
        module: 'roles',
        action: 'role.cloned',
        summary: `Created the ${name} role from ${source.name}`,
        recordType: 'role',
        recordId: role.id,
        recordLabel: name,
        severity: 'critical',
      });

      return role;
    });
  },

  /**
   * Change what a role permits.
   *
   * A reason rather than an actor, for the same reason as everywhere else: the
   * service knows who is signed in, and a change to a role changes it for
   * everybody holding it at once — so "what changed and why" is the pair the
   * audit entry needs, not just who was at the keyboard.
   */
  async setPermissions(id: string, permissions: string[], reason: string): Promise<RoleDefinition> {
    return serviceCall('rolePermissionService', async () => {
      const session = requireSession();
      const actor: AuditActor = {
        userId: session.userId,
        name: session.displayName,
        roles: session.roles,
      };
      const before = readOne<RoleDefinition>('roles', id);
      if (!before) throw errors.notFound('role');

      // The super administrator role is what recovers every other mistake.
      if (before.key === 'super_administrator') {
        throw errors.precondition(
          'The Super Administrator role cannot be edited — it is the role that recovers every other mistake.',
        );
      }

      const after = await update<RoleDefinition>(
        'roles',
        id,
        (row) => ({ ...row, permissions, updatedAt: runtime.now() }),
        'role',
      );

      const added = permissions.filter((permission) => !before.permissions.includes(permission));
      const removed = before.permissions.filter((permission) => !permissions.includes(permission));

      await recordAudit({
        actor,
        module: 'roles',
        action: 'role.permissions_changed',
        summary: `Updated ${after.name}: ${countLabel(added.length, 'permission')} added, ${removed.length} removed`,
        recordType: 'role',
        recordId: id,
        recordLabel: after.name,
        before: { permissions: before.permissions },
        after: { permissions },
        metadata: { added, removed, reason },
        severity: 'critical',
      });

      return after;
    });
  },
};

/* -------------------------------------------------------------------------- */
/* Payroll configuration                                                       */
/* -------------------------------------------------------------------------- */

export interface ConfigTable {
  id: string;
  name: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  demonstrationOnly: true;
  createdAt: string;
  createdByUserId: string;
  rows: Record<string, unknown>[];
}

/*
 * The prototype's configuration, presented in the engine's bracket shape.
 *
 * The prototype stores display rows (peso amounts as money objects, a tax
 * "rate percent"); the page reads brackets — a salary range and, per side, a
 * fixed amount or a share — because that is what the API sends and what a pay
 * run actually uses. The conversion is here, in both directions, so the page
 * has one shape to render. The prototype has no rate rules or calendar policy.
 */
const pesosOf = (value: unknown): string =>
  String(((value as { minorUnits?: number } | null)?.minorUnits ?? 0) / 100);

function kindOf(name: string): PayrollTableKind {
  const lower = name.toLowerCase();
  if (lower.includes('sss')) return 'sss';
  if (lower.includes('philhealth')) return 'philhealth';
  if (lower.includes('pag-ibig') || lower.includes('pagibig')) return 'pagibig';
  if (lower.includes('tax')) return 'withholding_tax';
  return 'other';
}

function toBrackets(table: ConfigTable): PayrollBracket[] {
  return table.rows.map((row) =>
    'employeeShare' in row
      ? {
          from: pesosOf(row.salaryFrom),
          to: row.salaryTo ? pesosOf(row.salaryTo) : null,
          employeeFixed: pesosOf(row.employeeShare),
          employerFixed: pesosOf(row.employerShare),
        }
      : {
          from: pesosOf(row.incomeFrom),
          to: row.incomeTo ? pesosOf(row.incomeTo) : null,
          employeeFixed: pesosOf(row.baseTax),
          employeeShare: String(Number(row.ratePercent ?? 0) / 100),
        },
  );
}

function fromBrackets(source: ConfigTable, brackets: PayrollBracket[]): Record<string, unknown>[] {
  const money = (pesos: string | undefined) => ({
    minorUnits: Math.round(Number(pesos ?? 0) * 100),
    currency: 'PHP',
  });
  return brackets.map((bracket, index) => {
    const original = source.rows[index] ?? {};
    return 'employeeShare' in original || !('incomeFrom' in original)
      ? {
          ...original,
          employeeShare: money(bracket.employeeFixed),
          employerShare: money(bracket.employerFixed),
        }
      : {
          ...original,
          baseTax: money(bracket.employeeFixed),
          ratePercent: Math.round(Number(bracket.employeeShare ?? 0) * 100),
        };
  });
}

const mockPayrollConfigService = {
  async configuration(): Promise<PayrollConfigurationView> {
    return serviceCall('payrollConfigService', () => ({
      tables: readAll<ConfigTable>('payrollConfig').map((table) => ({
        id: table.id,
        kind: kindOf(table.name),
        name: table.name,
        version: 1,
        effectiveFrom: table.effectiveFrom,
        effectiveTo: table.effectiveTo,
        brackets: toBrackets(table),
        legallyValidated: false,
        notes: null,
      })),
      rateRules: [],
      calendarPolicies: [],
    }));
  },

  /**
   * Save a new version rather than editing the current one.
   *
   * A contribution table that changes mid-year must not silently rewrite what
   * March was calculated against. The existing version is closed off the day
   * before the new one starts, so a past payroll run stays explicable.
   */
  async publishVersion(
    sourceId: string,
    input: { effectiveFrom: string; brackets: PayrollBracket[]; notes?: string },
  ): Promise<PayrollTableVersion> {
    const session = requireSession();
    const actor: AuditActor = {
      userId: session.userId,
      name: session.displayName,
      roles: session.roles,
    };
    return serviceCall('payrollConfigService', async () => {
      const source = readOne<ConfigTable>('payrollConfig', sourceId);
      if (!source) throw errors.notFound('configuration table');

      if (new Date(input.effectiveFrom) <= new Date(source.effectiveFrom)) {
        throw errors.validation(
          'A new version must take effect after the version it replaces, or the two would overlap.',
        );
      }

      const dayBefore = new Date(input.effectiveFrom);
      dayBefore.setDate(dayBefore.getDate() - 1);

      await update<ConfigTable>(
        'payrollConfig',
        sourceId,
        (row) => ({ ...row, effectiveTo: dayBefore.toISOString().slice(0, 10) }),
        'configuration table',
      );

      const rows = fromBrackets(source, input.brackets);
      const version: ConfigTable = {
        ...source,
        id: nextId('cfg'),
        effectiveFrom: input.effectiveFrom,
        effectiveTo: null,
        rows,
        createdAt: runtime.now(),
        createdByUserId: actor.userId,
      };
      await insert('payrollConfig', version);

      await recordAudit({
        actor,
        module: 'payroll',
        action: 'payroll.configuration_versioned',
        summary: `Published a new ${source.name} version effective ${input.effectiveFrom}`,
        recordType: 'payroll_configuration',
        recordId: version.id,
        recordLabel: source.name,
        before: { effectiveFrom: source.effectiveFrom, rows: source.rows },
        after: { effectiveFrom: input.effectiveFrom, rows },
        severity: 'critical',
      });

      return {
        id: version.id,
        kind: kindOf(version.name),
        name: version.name,
        version: 1,
        effectiveFrom: version.effectiveFrom,
        effectiveTo: null,
        brackets: input.brackets,
        legallyValidated: false,
        notes: null,
      };
    });
  },
};

/**
 * Payroll configuration. Against the API: GET /payroll/configuration for the
 * tables, rate rules and calendar policy the engine reads, and POST
 * /payroll/contribution-tables/:id/versions to publish a table version.
 */
export const payrollConfigService: typeof mockPayrollConfigService =
  env().dataSource === 'api' ? apiPayrollConfigService : mockPayrollConfigService;

export interface CompanySettings {
  id: string;
  company: Record<string, string>;
  employeeNumbering: Record<string, unknown>;
  attendance: Record<string, unknown>;
  approvals: Record<string, boolean>;
  session: Record<string, unknown>;
  /** API mode: the employee counters' next values, for the numbering preview. */
  employeeNumberNext?: { year: number; yearly: number; continuous: number };
  updatedAt: string;
}

export interface SaveSettingsOptions {
  /** Required by the server; the mock records it in the same audit shape. */
  reason: string;
  /** Only meaningful when enabling continuous location tracking. */
  acknowledgeTrackingPolicy?: boolean;
}

const mockSettingsService = {
  async get(): Promise<CompanySettings> {
    return serviceCall('settingsService', () => {
      const rows = readAll<CompanySettings>('settings');
      if (rows.length === 0) throw errors.notFound('settings');
      return rows[0]!;
    });
  },

  async save(
    section: keyof CompanySettings,
    values: Record<string, unknown>,
    options: SaveSettingsOptions,
  ) {
    return serviceCall('settingsService', async () => {
      const session = requireSession();
      const rows = readAll<CompanySettings>('settings');
      const before = rows[0];
      if (!before) throw errors.notFound('settings');

      const next = { ...before, [section]: values, updatedAt: runtime.now() } as CompanySettings;
      await writeAll('settings', [next]);

      await recordAudit({
        actor: { userId: session.userId, name: session.displayName, roles: session.roles },
        module: 'settings',
        action: 'settings.updated',
        summary: `Updated ${String(section)} settings`,
        recordType: 'settings',
        recordId: 'settings',
        recordLabel: String(section),
        before: { [section]: before[section] },
        after: { [section]: values },
        metadata: { reason: options.reason },
        severity: 'critical',
      });

      return next;
    });
  },

  /** Every edited section at once. See the API facade for why. */
  async saveMany(
    changes: Partial<Record<SettingsSectionKey, Record<string, unknown>>>,
    options: SaveSettingsOptions,
  ): Promise<CompanySettings> {
    let latest: CompanySettings | null = null;
    for (const [section, values] of Object.entries(changes)) {
      if (values)
        latest = await mockSettingsService.save(section as SettingsSectionKey, values, options);
    }
    return latest ?? mockSettingsService.get();
  },
};

/** The sections of Company Settings that hold editable values. */
export type SettingsSectionKey =
  'company' | 'employeeNumbering' | 'attendance' | 'approvals' | 'session';

/**
 * Settings, against the API.
 *
 * Every section is the server's: the company profile from the organisation,
 * everything else from its settings row. Saving routes each section to its
 * endpoint; a section the server refuses (inverted thresholds, a geofence
 * tighter than GPS accuracy, tracking without an acknowledgement, a prefix
 * with punctuation in it) surfaces the server's own explanation.
 */
const apiSettingsFacade = {
  async get(): Promise<CompanySettings> {
    const [profile, stored] = await Promise.all([
      apiSettingsService.profile(),
      apiSettingsService.attendancePolicy(),
    ]);

    return {
      id: 'settings',
      company: apiSettingsService.toCompanySection(profile),
      attendance: toAttendanceSection(stored),
      employeeNumbering: toSection(stored, 'employeeNumbering'),
      approvals: toSection(stored, 'approvals') as Record<string, boolean>,
      session: toSection(stored, 'session'),
      employeeNumberNext: stored.employeeNumberNext,
      updatedAt: stored.updatedAt,
    };
  },

  async save(
    section: keyof CompanySettings,
    values: Record<string, unknown>,
    options: SaveSettingsOptions,
  ) {
    if (section === 'company') {
      await apiSettingsService.saveCompany(values, options.reason);
    } else if (section === 'attendance') {
      await apiSettingsService.saveAttendance(
        values,
        options.reason,
        options.acknowledgeTrackingPolicy ?? false,
      );
    } else if (isServerSection(section)) {
      await apiSettingsService.saveSection(section, values, options.reason);
    } else {
      throw new Error(`Settings section “${String(section)}” cannot be saved.`);
    }
    return apiSettingsFacade.get();
  },

  /**
   * Every edited section, from one Save.
   *
   * The settings-row sections go in a single request — validated together,
   * audited together, applied together — and the company profile, which
   * lives on the organisation, after it. An edit is never dropped because it
   * sat in a section other than the one on screen when Save was pressed.
   */
  async saveMany(
    changes: Partial<Record<SettingsSectionKey, Record<string, unknown>>>,
    options: SaveSettingsOptions,
  ): Promise<CompanySettings> {
    const { company, ...rowSections } = changes;
    // One save, one request, one audit entry — the company profile rides along
    // with the settings row rather than being a second entry of its own.
    if (Object.values(rowSections).some(Boolean)) {
      await apiSettingsService.saveSettings(
        rowSections,
        options.reason,
        options.acknowledgeTrackingPolicy ?? false,
        company,
      );
    } else if (company) {
      await apiSettingsService.saveCompany(company, options.reason);
    }
    return apiSettingsFacade.get();
  },
};

export const settingsService: typeof mockSettingsService =
  env().dataSource === 'api' ? apiSettingsFacade : mockSettingsService;

/**
 * The idle timeout that applies to the signed-in user.
 *
 * Its own service because the settings above are behind
 * `settings.configure`, and the timeout has to reach every signed-in user.
 */
export const sessionPolicyService = {
  async get(): Promise<{ idleTimeoutMinutes: number }> {
    if (env().dataSource === 'api') {
      const policy = await apiSettingsService.sessionPolicy();
      // Read once after sign-in by the shell, so it is also where the
      // organisation's calendar reaches every "today" default in the console.
      setBusinessTimeZone(policy.timeZone);
      return policy;
    }
    const stored = readAll<CompanySettings>('settings')[0];
    return { idleTimeoutMinutes: Number(stored?.session?.idleTimeoutMinutes) || 30 };
  },

  /**
   * Tell the server the console is in use. Any authenticated request does —
   * the server records it as the session's last activity, which is what its
   * idle timeout is measured from. Nothing to tell the mock.
   */
  async keepAlive(): Promise<void> {
    if (env().dataSource === 'api') await apiSettingsService.sessionPolicy();
  },
};

export const compensationService: typeof mockCompensationService =
  env().dataSource === 'api'
    ? { ...mockCompensationService, ...apiCompensationService }
    : mockCompensationService;

/**
 * Recruitment.
 *
 * `convertToEmployee` now has one signature on both sides: the candidate and
 * where they are going. Everything else — names, contact details, the start
 * date from the accepted offer — the service works out, and the employee,
 * employment history, salary and onboarding case are created together.
 */
export const recruitmentService: typeof mockRecruitmentService =
  env().dataSource === 'api'
    ? {
        ...mockRecruitmentService,
        /*
         * The whole surface the screens call, under the prototype's exact
         * names and signatures: `requisitions`, `createRequisition`,
         * `approveRequisition`, `candidates`, `candidate`, `dashboard`,
         * `moveStage` and `convertToEmployee`. The audit actors the mock takes
         * are accepted and never sent — the server resolves the caller from
         * the session — and the stage machine is validated server-side, not
         * duplicated here.
         */
        ...apiRecruitmentService,
      }
    : mockRecruitmentService;

/**
 * Roles and permissions.
 *
 * The whole surface comes from the API when connected. The prototype's rule
 * about the super administrator role stays beneath for the mock data source;
 * the server's equivalent is broader — it refuses any change that would leave
 * no active role able to manage users, which covers the same ground without
 * naming one role as special.
 *
 * `clone` stays on the prototype: the API has no create-role route — no POST
 * /roles or /roles/:id/clone — so a cloned role would exist in a browser and
 * nowhere else.
 */
export const rolePermissionService: typeof mockRolePermissionService =
  env().dataSource === 'api'
    ? { ...mockRolePermissionService, ...apiRolePermissionService }
    : mockRolePermissionService;

/**
 * Assets. List, summary, assign, return and status changes come from the API.
 * `setStatus` used to stay on the mock, which would happily mark an asset
 * available while an assignment row said somebody still held it; the server
 * refuses that.
 */
export const assetService: typeof mockAssetService =
  env().dataSource === 'api' ? { ...mockAssetService, ...apiAssetService } : mockAssetService;

/**
 * Policies. List, publish and acknowledgement progress all come from the API.
 * The progress figures used to fall to the mock — prototype numbers printed
 * beside real policies — because acknowledgements were writable and
 * unreadable. `GET /policies/:id/acknowledgements` closes that.
 */
export const policyService: typeof mockPolicyService =
  env().dataSource === 'api' ? { ...mockPolicyService, ...apiPolicyService } : mockPolicyService;

/** Announcements. List and publish come from the API. */
export const announcementAdminService: typeof mockAnnouncementAdminService =
  env().dataSource === 'api'
    ? { ...mockAnnouncementAdminService, ...apiAnnouncementService }
    : mockAnnouncementAdminService;

/** Documents. List, detail, verification and the summary come from the API. */
export const documentAdminService: typeof mockDocumentAdminService =
  env().dataSource === 'api'
    ? {
        ...mockDocumentAdminService,
        ...apiDocumentAdminService,
        // The summary counts the same list the table renders. Without this
        // the rail announced the prototype's 619 documents above a table
        // honestly reporting none.
        ...apiDocumentSummaryService,
      }
    : mockDocumentAdminService;

/**
 * Onboarding. The case list and task completion come from the API; the case
 * auto-closes through the server's guarded route once its last task is done.
 * `progressOf` is pure arithmetic and stays shared from the prototype.
 */
export const onboardingService: typeof mockOnboardingService =
  env().dataSource === 'api'
    ? { ...mockOnboardingService, ...apiOnboardingService }
    : mockOnboardingService;

/**
 * Probation. List, summary and the decision come from the API — the summary is
 * derived from the same list the table renders, since no probation aggregation
 * route exists.
 */
export const probationService: typeof mockProbationService =
  env().dataSource === 'api'
    ? { ...mockProbationService, ...apiProbationService }
    : mockProbationService;

/**
 * Offboarding. List, clearance tasks and closing come from the API; the
 * cannot-close-with-outstanding-clearance rule is the server's.
 */
export const offboardingService: typeof mockOffboardingService =
  env().dataSource === 'api'
    ? { ...mockOffboardingService, ...apiOffboardingService }
    : mockOffboardingService;

/**
 * Loans. Register, per-employee view and summary come from the API — the
 * latter two derived from the same register, since no per-employee or
 * aggregation route exists. Status transitions (POST /loans/:id/approve etc.)
 * have server routes but no method on this service yet — no screen calls one.
 */
export const loanService: typeof mockLoanService =
  env().dataSource === 'api' ? { ...mockLoanService, ...apiLoanService } : mockLoanService;

/**
 * Performance. The review list, finalisation and the cycle list all come from
 * the API. Cycles used to exist server-side only as a relation hanging off a
 * review, so the screens could name a review's cycle but not list them.
 */
export const performanceService: typeof mockPerformanceService =
  env().dataSource === 'api'
    ? { ...mockPerformanceService, ...apiPerformanceService }
    : mockPerformanceService;

/**
 * Employee relations. The case list, detail and timeline writes come from the
 * API — names joined from the directory, confidential notes exactly as the
 * server decided, and every entry recorded against the session's account.
 * Each case arrives with its timeline, so an appended entry reads straight
 * back.
 */
export const employeeRelationsService: typeof mockEmployeeRelationsService =
  env().dataSource === 'api'
    ? { ...mockEmployeeRelationsService, ...apiEmployeeRelationsService }
    : mockEmployeeRelationsService;

/**
 * Benefits. The plan catalogue, the enrolment list and enrolling all go to the
 * API. The screen used to show a real list of plans beside prototype
 * membership, and "Enrol" wrote to browser storage.
 */
export const benefitsService: typeof mockBenefitsService =
  env().dataSource === 'api'
    ? { ...mockBenefitsService, ...apiBenefitsService }
    : mockBenefitsService;

/**
 * Learning. The course catalogue, completions, training records and the
 * summary come from the API. `sessions` stays on the mock — the server has no
 * session model at all, so scheduled classroom sessions read empty in API
 * mode rather than pretending otherwise.
 */
export const learningService: typeof mockLearningService =
  env().dataSource === 'api'
    ? { ...mockLearningService, ...apiLearningService }
    : mockLearningService;

/**
 * Leave configuration. Types, balances, their ledger and adjustments all come
 * from the API (see `apiLeaveConfigService`). The screen's `LeaveType` is the
 * seven fields the server also keeps, so the mapping invents nothing.
 */
export const leaveConfigService: typeof mockLeaveConfigService =
  env().dataSource === 'api'
    ? { ...mockLeaveConfigService, ...apiLeaveConfigService }
    : mockLeaveConfigService;
